import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Collapse, Button, Well } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import cloneDeep from 'lodash.clonedeep';
import { useRequestData, useLabels } from 'wsm-common-data';
import PropTypes from 'prop-types';
import { trackEvent } from 'ddc-track-event';
import logger from 'ws-scripts/modules/logger';
import Select from './Select';
import SelectItemLoader from './SelectItemLoader';
import {
	setTrim,
	setPrice,
	setImage,
	setDrivetrain,
	setCab,
	setOptions,
	setVehicleConfiguration,
	setPaint,
	setPackages
} from '../actions/setVehicleConfiguration';
import { setTrimData } from '../actions/setTrimData';
import {
	setEnableContinueBtn,
	setDisableContinueBtn
} from '../actions/setContinueBtn';
import { initVehicleConfiguration } from '../server/data/defaults';
import { formatter, getModelKey, getResizedImageUrl } from '../helpers';
import { API } from '../api/services';
import {
	DIESEL_VALUE,
	DEFAULT_IMAGE_WIDTH,
	DEFAULT_IMAGE_WIDTH_MOBILE_TRAY
} from '../constants';
import stepController from '../controllers/StepController';

const Trim = ({ setCurrentStep }) => {
	const labels = useLabels();
	const dispatch = useDispatch();
	const { make: makeFromParams } = useParams();
	const navigate = useNavigate();

	const { accountData, trimData, vehicleConfiguration, rootDir } =
		useSelector((state) => state);

	const {
		make: makeFromState,
		model: modelFromState,
		trimName: trimFromState,
		options: optionsFromState
	} = vehicleConfiguration;

	const { franchiseModels, accountId } = accountData;
	const { locale, deviceType, widgetName, windowId } = useRequestData();

	const isMobile = deviceType === 'MOBILE';
	const hasTrimDataNotChange =
		Object.keys(trimData).length !== 0 &&
		trimData[0].make === makeFromState &&
		trimData[0].model === modelFromState;

	const [selectedIndex, setSelectedIndex] = useState(null);
	const [trimArrayData, setTrimArrayData] = useState([]);
	const [detailsOpenIndexes, setDetailsOpenIndexes] = useState([]);

	const getTrimName = ({ trim }) => {
		return trim || 'Base';
	};

	const missingConfig = () => {
		return (
			!vehicleConfiguration?.make ||
			!vehicleConfiguration?.model ||
			!vehicleConfiguration?.year
		);
	};

	// auto-select trim when there is just one option
	const selectTrimOption = (trimSelectionDataArray) => {
		if (Object.keys(trimSelectionDataArray).length === 1) {
			setSelectedIndex(0);
			dispatch(setTrim(trimSelectionDataArray[0].trim));
			dispatch(setPrice(trimSelectionDataArray[0].price));
			dispatch(setImage(trimSelectionDataArray[0].imageUrl));
			dispatch(setOptions(cloneDeep(initVehicleConfiguration.options)));
			dispatch(setEnableContinueBtn(true));
		}
		stepController.updateLsVehicleConfig();
	};

	useEffect(() => {
		setCurrentStep('trim');
	}, [setCurrentStep]);

	// This useEffect is used to check the vehicle config and/or local storage for the values we need
	// to show trim data.
	// If we don't have the values we need, we redirect to make
	useEffect(() => {
		const lsVehicleConfig = stepController.getLsVehicleConfig();

		// if we have make/model/year in local storage, use that
		if (
			missingConfig() &&
			lsVehicleConfig != null &&
			lsVehicleConfig.make &&
			lsVehicleConfig.year &&
			lsVehicleConfig.model
		) {
			dispatch(
				setVehicleConfiguration({
					...lsVehicleConfig
				})
			);
		}
		// if we don't have make/model/year from local storage OR redux,
		// we can't get trim data
		// use make from params and redirect to previous step
		else if (missingConfig()) {
			const makeScreenPath = `/${rootDir}/${makeFromParams}.htm`;
			navigate(makeScreenPath);
		}
	}, []);

	useEffect(() => {
		let resolveTrimsData;
		const trimSelectionDataArray = [];
		const alltrimsDataArray = [];
		const uniqueTrims = {};
		let findIndex;

		// TODO: check vehicleConfig to ensure we have year/make/model
		// this line errors after refreshing
		const modelKey = getModelKey(
			vehicleConfiguration.year,
			vehicleConfiguration.make,
			vehicleConfiguration.model
		);
		// check if trim is selected
		if (hasTrimDataNotChange) {
			resolveTrimsData = trimData;

			resolveTrimsData.forEach((singleTrim) => {
				// This needs to be improved - GV data is inconsistent between environments
				const trimName = getTrimName(singleTrim);
				const {
					id,
					imageUrl,
					price,
					description,
					driveTrain,
					cabType,
					fuelType,
					make,
					model
				} = singleTrim;
				// we want to show only unique trims
				const uniqueTrimName = `${trimName}_${fuelType}`;
				if (!uniqueTrims[uniqueTrimName]) {
					uniqueTrims[uniqueTrimName] = true;
					// trims for display
					trimSelectionDataArray.push({
						id,
						imageUrl,
						trim: trimName,
						price,
						description,
						driveTrain,
						cabType,
						fuelType,
						make,
						model
					});
				}
			});
			setTrimArrayData(trimSelectionDataArray);
			if (trimFromState) {
				findIndex = trimSelectionDataArray.findIndex(
					(element) => element.trim === trimFromState
				);
			}
			selectTrimOption(trimSelectionDataArray);
		} else {
			const options = {
				fields: [
					'msrPrice',
					'images',
					'globalVehicleTrimId',
					'description',
					'driveTrain',
					'cabType',
					'trim',
					'fuelType',
					'make',
					'model'
				]
			};

			const getGvModelsSync = async () => {
				const modelData =
					franchiseModels[vehicleConfiguration.make.toLowerCase()]
						?.data[modelKey];

				if (modelData) {
					try {
						const { globalVehicleId } = modelData;
						resolveTrimsData = await API.getGVShowroomVehicle(
							globalVehicleId,
							accountId,
							locale,
							options
						);

						resolveTrimsData.forEach((singleTrim) => {
							// This needs to be improved - GV data is inconsistent between environments
							const trimName = getTrimName(singleTrim);
							const {
								globalVehicleTrimId,
								imageUrl,
								msrPrice,
								description,
								driveTrain,
								cabType,
								fuelType,
								make,
								model
							} = singleTrim;
							// we want to show only unique trims
							const uniqueTrimName = `${trimName}_${fuelType}`;
							if (!uniqueTrims[uniqueTrimName]) {
								uniqueTrims[uniqueTrimName] = true;
								// trims for display
								trimSelectionDataArray.push({
									id: globalVehicleTrimId,
									imageUrl,
									trim: trimName,
									price: msrPrice,
									description,
									driveTrain,
									cabType,
									fuelType,
									make,
									model
								});
							}
							// trims for redux store
							alltrimsDataArray.push({
								id: globalVehicleTrimId,
								imageUrl,
								trim: trimName,
								price: msrPrice,
								description,
								driveTrain,
								cabType,
								fuelType,
								make,
								model
							});
						});

						setTrimArrayData(trimSelectionDataArray);
						dispatch(setTrimData(alltrimsDataArray));
						setSelectedIndex(null);
						dispatch(setTrim(null));
						dispatch(
							setDrivetrain({
								value: null,
								keyLabel: 'Drive Type',
								valueLabel: 'TBD',
								modified: false
							})
						);
						dispatch(
							setPaint({
								value: null,
								keyLabel: 'Paint',
								valueLabel: 'TBD',
								modified: false
							})
						);
						dispatch(
							setPackages({
								keyLabel: 'Packages',
								valueLabel: 'TBD',
								modified: false,
								selections: []
							})
						);
						if (typeof optionsFromState?.cab !== 'undefined') {
							dispatch(
								setCab({
									value: null,
									keyLabel: 'Cab Type',
									valueLabel: 'TBD',
									modified: false
								})
							);
						}

						// preload images
						const preloadImageWidth = isMobile
							? DEFAULT_IMAGE_WIDTH_MOBILE_TRAY
							: DEFAULT_IMAGE_WIDTH;
						trimSelectionDataArray.forEach(
							({ imageUrl: trimImage }) => {
								if (trimImage) {
									const img = new Image();
									img.src = getResizedImageUrl(
										trimImage,
										preloadImageWidth
									);
								}
							}
						);

						selectTrimOption(trimSelectionDataArray);
					} catch (err) {
						logger.error(err);
						const makeScreenPath = `/${rootDir}/${makeFromParams}.htm`;
						navigate(makeScreenPath);
					}
				}
			};

			getGvModelsSync();
		}

		// enable/disable continue button
		if (typeof findIndex === 'undefined' || findIndex < 0) {
			setSelectedIndex(null);
			dispatch(setDisableContinueBtn(true));
		} else if (findIndex >= 0 && hasTrimDataNotChange) {
			setSelectedIndex(findIndex);
			dispatch(setEnableContinueBtn(true));
		}
	}, [makeFromState, accountId, locale]);

	const handleSelect = (event, itemIndex) => {
		if (event.target.checked) {
			dispatch(setEnableContinueBtn(true));
		}
		const { trim, price, imageUrl } = trimArrayData[itemIndex];
		setSelectedIndex(itemIndex);
		dispatch(setTrim(trim));
		dispatch(setPrice(price));
		dispatch(setImage(imageUrl));
		dispatch(setOptions(cloneDeep(initVehicleConfiguration.options)));
		trackEvent(widgetName, windowId, {
			element: 'Trim option',
			elementCTA: trim,
			action: 'selected',
			result: 'Trim radio button selected'
		});
		stepController.updateLsVehicleConfig();
	};

	const handleToggleDetails = (index) => {
		const updatedIndexes = [...detailsOpenIndexes];
		const arrayIndex = detailsOpenIndexes.indexOf(index);
		if (arrayIndex > -1) {
			updatedIndexes.splice(arrayIndex, 1);
		} else {
			updatedIndexes.push(index);
		}
		setDetailsOpenIndexes(updatedIndexes);
		trackEvent(widgetName, windowId, {
			element: 'Trim option',
			elementCTA: trimArrayData[index].trimName,
			action: 'clicked',
			result: 'Trim details expanded'
		});
	};

	if (trimArrayData.length === 0) {
		return <SelectItemLoader count={3} />;
	}

	return (
		<React.Fragment>
			<form>
				{trimArrayData.map((data, index) => {
					const detail = `${labels.get(
						'STARTING_AT'
					)} ${formatter.format(data.price)}`;

					// if fuel type is diesel, add it to the trim name
					const fuelTypeText =
						data.fuelType === DIESEL_VALUE ? (
							<small>{data.fuelType}</small>
						) : null;

					// We need to come up with a better method of data handling here
					const updatedData = {
						...data,
						value: (
							<>
								{data.trim} {fuelTypeText}
							</>
						)
					};

					return (
						<React.Fragment key={`${data.id}.select.container`}>
							<Select
								data={updatedData}
								detail={detail}
								inputType="radio"
								itemIndex={index}
								selected={index === selectedIndex}
								handleSelect={handleSelect}
								sideContent={
									<Button
										className="btn-link font-weight-normal"
										onClick={() =>
											handleToggleDetails(index)
										}
										aria-controls={`collapseDetails${index}`}
										aria-expanded={detailsOpenIndexes.includes(
											index
										)}
									>
										<small>{labels.get('DETAILS')}</small>
									</Button>
								}
							/>
							<Collapse in={detailsOpenIndexes.includes(index)}>
								<div>
									<Well className="text-left">
										{data.description ||
											labels.get(
												'TRIM_DESCRIPTION_UNAVAILABLE'
											)}
									</Well>
								</div>
							</Collapse>
						</React.Fragment>
					);
				})}
			</form>
		</React.Fragment>
	);
};

Trim.propTypes = {
	setCurrentStep: PropTypes.func
};

export default Trim;
